import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { apiGet, apiPatch, apiPost } from "../../processes/helpers/api";
import { getCurrentUser } from "../../selectors/userSelectors";
import { notifySuccess, dismissNotifier } from "../../processes/notifierProcesses";
import TeacherMocksPage from "./TeacherMocksPage";
import { MOCKS_CACHE_TTL, defaultForm } from "./teacherMocksConstants";
import {
  buildMockTemplatePayload,
  copyText,
  managerQuery,
  mapQuestionsToUpdateAttributes,
  mapTemplateToFormValues,
  mapTemplateToTeacherMock,
  mergeTemplateQuestionIds,
  mockTemplatesQuery,
} from "./teacherMocksHelpers";

export default function TeacherMocksPageContainer() {
  const currentUser = useSelector(getCurrentUser);
  const dispatch = useDispatch();

  useEffect(() => {
    document.body.classList.add("lms-teacher-mocks-page");
    return () => document.body.classList.remove("lms-teacher-mocks-page");
  }, []);

  const [activeStatus, setActiveStatus] = useState("enabled");
  const [searchTerm, setSearchTerm] = useState("");
  const [query, setQuery] = useState("");
  const [courseId, setCourseId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false);
  const [viewMockValues, setViewMockValues] = useState(defaultForm);
  const [isLoadingViewMock, setIsLoadingViewMock] = useState(false);
  const [mocks, setMocks] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [statusCounts, setStatusCounts] = useState({ enabled: 0, draft: 0, disabled: 0 });
  const [isLoadingMocks, setIsLoadingMocks] = useState(false);
  const [mockTemplatesError, setMockTemplatesError] = useState("");
  const [page, setPage] = useState(1);
  const [managerCourses, setManagerCourses] = useState([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [togglingIds, setTogglingIds] = useState(new Set());
  const [rankingDrawer, setRankingDrawer] = useState({
    open: false,
    mock: null,
    ranking: [],
    loading: false,
    error: "",
  });
  const [publicationSuccess, setPublicationSuccess] = useState(null);
  const [lastCreatedId, setLastCreatedId] = useState(null);

  const mocksCache = useRef(new Map());
  const lastCreatedMockRef = useRef(null);

  const getCached = useCallback((status, q, cId, p) => {
    const key = `${status}|${q.trim()}|${cId}|${p}`;
    const entry = mocksCache.current.get(key);
    if (!entry || Date.now() - entry.timestamp > MOCKS_CACHE_TTL) {
      mocksCache.current.delete(key);
      return null;
    }
    return entry;
  }, []);

  const setCached = useCallback((status, q, cId, p, data, paginationData) => {
    mocksCache.current.set(`${status}|${q.trim()}|${cId}|${p}`, {
      data,
      pagination: paginationData,
      timestamp: Date.now(),
    });
  }, []);

  const invalidateCache = useCallback((...statuses) => {
    for (const status of statuses) {
      for (const key of mocksCache.current.keys()) {
        if (key.startsWith(`${status}|`)) mocksCache.current.delete(key);
      }
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setQuery(searchTerm);
    }, 350);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    let isMounted = true;

    async function loadCourses() {
      try {
        setIsLoadingCourses(true);
        const q = managerQuery({
          "q[question_version_two_count_gt]": 0,
          "q[s]": "name asc",
          paginated: false,
        });
        const response = await apiGet(`/api/v2/manager/courses?${q}`);
        if (isMounted) setManagerCourses(response.body?.data || []);
      } catch {
        if (isMounted) setManagerCourses([]);
      } finally {
        if (isMounted) setIsLoadingCourses(false);
      }
    }

    loadCourses();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [activeStatus, courseId, query]);

  useEffect(() => {
    let isMounted = true;

    async function loadMockTemplates() {
      const cached = getCached(activeStatus, query, courseId, page);
      if (cached) {
        setMocks(cached.data);
        setPagination(cached.pagination);
        if (cached.pagination?.totalItems != null) {
          setStatusCounts((current) => ({ ...current, [activeStatus]: cached.pagination.totalItems }));
        }
        setMockTemplatesError("");
        setIsLoadingMocks(false);
        return;
      }

      try {
        setIsLoadingMocks(true);
        setMockTemplatesError("");
        const queryString = mockTemplatesQuery({ activeStatus, query, courseId, page });
        const response = await apiGet(`/api/v2/manager/mock_templates?${queryString}`);
        if (!isMounted) return;
        const data = (response.body?.data || []).map(mapTemplateToTeacherMock);
        const paginationData = response.body?.pagination || null;
        const pinnedMock = lastCreatedMockRef.current;
        const finalData = pinnedMock && activeStatus === "enabled" && !data.some((m) => m.id === pinnedMock.id)
          ? [pinnedMock, ...data]
          : data;
        setMocks(finalData);
        setPagination(paginationData);
        if (paginationData?.totalItems != null) {
          setStatusCounts((current) => ({
            ...current,
            [activeStatus]: paginationData.totalItems,
          }));
        }
        setCached(activeStatus, query, courseId, page, data, paginationData);
      } catch {
        if (!isMounted) return;
        setMocks([]);
        setPagination(null);
        setMockTemplatesError("Nao foi possivel carregar os simulados. Tente novamente.");
      } finally {
        if (isMounted) setIsLoadingMocks(false);
      }
    }

    loadMockTemplates();

    return () => {
      isMounted = false;
    };
  }, [activeStatus, courseId, page, query, getCached, setCached]);

  useEffect(() => {
    let isMounted = true;

    async function loadStatusCounts() {
      try {
        const statusesToLoad = ["enabled", "draft", "disabled"].filter((status) => status !== activeStatus);
        const responses = await Promise.all(
          statusesToLoad.map((status) => {
            const queryString = mockTemplatesQuery({
              activeStatus: status,
              query,
              courseId,
              page: 1,
              perPage: 1,
            });
            return apiGet(`/api/v2/manager/mock_templates?${queryString}`).then((response) => [status, response]);
          })
        );

        if (!isMounted) return;
        setStatusCounts((current) => ({
          ...current,
          ...Object.fromEntries(
            responses.map(([status, response]) => [status, response.body?.pagination?.totalItems || 0])
          ),
        }));
      } catch {
        if (isMounted) setStatusCounts({ enabled: 0, draft: 0, disabled: 0 });
      }
    }

    loadStatusCounts();

    return () => {
      isMounted = false;
    };
  }, [activeStatus, courseId, query]);

  const courseOptions = useMemo(() => {
    if (managerCourses.length) return managerCourses;
    return currentUser?.meta?.courses || [];
  }, [currentUser?.meta?.courses, managerCourses]);

  const handleToggleEnabled = useCallback(async (mock) => {
    const newEnabled = mock.status !== "enabled";
    const fromStatus = mock.status;
    const toStatus = newEnabled ? "enabled" : "disabled";

    setTogglingIds((prev) => new Set(prev).add(mock.id));
    try {
      await apiPatch(`/api/v2/manager/mock_templates/${mock.id}`).send({
        mockTemplate: { enabled: newEnabled },
      });
      setMocks((prev) => prev.filter((m) => m.id !== mock.id));
      setStatusCounts((prev) => ({
        ...prev,
        [fromStatus]: Math.max(0, (prev[fromStatus] || 0) - 1),
        [toStatus]: (prev[toStatus] || 0) + 1,
      }));
      invalidateCache(fromStatus, toStatus);
    } catch {
      // Silencioso — o item permanece na lista se a API falhar
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(mock.id);
        return next;
      });
    }
  }, [invalidateCache]);

  const openCreateModal = useCallback(() => {
    setModalMode("create");
    setViewMockValues(defaultForm);
    setIsLoadingViewMock(false);
    setIsModalOpen(true);
  }, []);

  const closeMockModal = useCallback(() => {
    setIsModalOpen(false);
    setModalMode("create");
    setViewMockValues(defaultForm);
    setIsLoadingViewMock(false);
  }, []);

  const closeViewDrawer = useCallback(() => {
    setIsViewDrawerOpen(false);
    setViewMockValues(defaultForm);
    setIsLoadingViewMock(false);
  }, []);

  const handleViewMock = useCallback(async (mock) => {
    const isDraft = mock.status === "draft";
    const initialValues = {
      ...defaultForm,
      id: mock.id,
      name: mock.name || "",
      courseId: mock.courseId ? String(mock.courseId) : "",
      courseName: mock.course || "",
      enabled: mock.status !== "disabled",
      draft: isDraft,
      questions: [],
    };

    setViewMockValues(initialValues);
    setIsLoadingViewMock(true);

    if (isDraft) {
      setModalMode("draft");
      setIsModalOpen(true);
    } else {
      setIsViewDrawerOpen(true);
    }

    try {
      const response = await apiGet(`/api/v2/manager/mock_templates/${mock.id}`);
      const template = response.body?.data;
      if (template) setViewMockValues(mapTemplateToFormValues(template));
    } catch {
      // Mantém os dados básicos da linha se a API de detalhes falhar.
    } finally {
      setIsLoadingViewMock(false);
    }
  }, []);

  const handleOpenMockLink = useCallback((mock) => {
    copyText(mock.publicUrl);
  }, []);

  const handleCopyMock = useCallback(async (mock) => {
    try {
      const response = await apiPost(`/api/v2/manager/mock_templates/${mock.id}/duplicate`).send({});
      const created = response.body?.data;
      invalidateCache("draft");
      if (created) {
        const createdMock = mapTemplateToTeacherMock(created);
        setStatusCounts((prev) => ({ ...prev, draft: (prev.draft || 0) + 1 }));
        setLastCreatedId(createdMock.id);
        setActiveStatus("draft");
        setPage(1);
        dispatch(notifySuccess({
          title: "Simulado duplicado",
          message: `"${createdMock.name}" salvo nos rascunhos`,
        }));
        setTimeout(() => {
          dispatch(dismissNotifier());
          setLastCreatedId(null);
        }, 3000);
      }
    } catch {
      // silencioso
    }
  }, [invalidateCache, dispatch]);

  const handlePublishDraft = useCallback(async (mock) => {
    try {
      const response = await apiPatch(`/api/v2/manager/mock_templates/${mock.id}`).send({
        mockTemplate: { draft: false, enabled: true },
      });
      const publishedData = response.body?.data;
      const publishedMock = publishedData ? mapTemplateToTeacherMock(publishedData) : null;
      setStatusCounts((prev) => ({
        ...prev,
        draft: Math.max(0, (prev.draft || 0) - 1),
        enabled: (prev.enabled || 0) + 1,
      }));
      invalidateCache("draft", "enabled");
      const mockId = publishedMock?.id || String(mock.id);
      lastCreatedMockRef.current = publishedMock;
      setLastCreatedId(mockId);
      setMocks([]);
      setActiveStatus("enabled");
      setPage(1);
      dispatch(notifySuccess({
        title: "Simulado publicado",
        message: `"${mock.name}" movido para Habilitados`,
      }));
      setTimeout(() => {
        dispatch(dismissNotifier());
        setLastCreatedId(null);
        lastCreatedMockRef.current = null;
      }, 3000);
    } catch {
      // silencioso
    }
  }, [invalidateCache, dispatch]);

  const handleViewRanking = useCallback(async (mock) => {
    if (!mock.hasRanking) return;

    setRankingDrawer({
      open: true,
      mock,
      ranking: [],
      loading: true,
      error: "",
    });

    try {
      let rankingMockId = mock.rankingMockId;
      if (!rankingMockId) {
        const response = await apiGet(`/api/v2/manager/mock_templates/${mock.id}`);
        rankingMockId = response.body?.data?.meta?.stats?.lastFinishedMockId;
      }

      if (!rankingMockId) {
        throw new Error("Ranking indisponível para este simulado.");
      }

      const rankingResponse = await apiGet(`/api/v1/mocks/${rankingMockId}/ranking`);
      setRankingDrawer({
        open: true,
        mock: { ...mock, rankingMockId },
        ranking: rankingResponse.body?.data || [],
        loading: false,
        error: "",
      });
    } catch (error) {
      setRankingDrawer({
        open: true,
        mock,
        ranking: [],
        loading: false,
        error: error.message || "Não foi possível carregar o ranking.",
      });
    }
  }, []);

  const closeRankingDrawer = useCallback(() => {
    setRankingDrawer((current) => ({ ...current, open: false }));
  }, []);

  const handleAutosaveDraft = useCallback(async (values) => {
    if (!values.courseId || values.questions.length === 0) return null;

    const payload = buildMockTemplatePayload(values, { draft: true });
    const response = values.id && values.draft
      ? await apiPatch(`/api/v2/manager/mock_templates/${values.id}`).send(payload)
      : await apiPost("/api/v2/manager/mock_templates").send(payload);
    const draft = response.body?.data;
    if (!draft) return null;

    const mappedDraft = mapTemplateToTeacherMock(draft);
    invalidateCache("draft");
    setStatusCounts((current) => ({
      ...current,
      draft: values.id && values.draft ? current.draft : current.draft + 1,
    }));
    if (activeStatus === "draft") {
      setMocks((current) => {
        const exists = current.some((mock) => mock.id === mappedDraft.id);
        return exists
          ? current.map((mock) => (mock.id === mappedDraft.id ? mappedDraft : mock))
          : [mappedDraft, ...current];
      });
    }

    return draft;
  }, [activeStatus, invalidateCache]);

  const handleCreate = async (values) => {
    let publishValues = values;
    if (values.id && values.draft) {
      const draftResponse = await apiGet(`/api/v2/manager/mock_templates/${values.id}`);
      publishValues = {
        ...values,
        questions: mergeTemplateQuestionIds(values.questions, draftResponse.body?.data),
      };
    }

    const payload = buildMockTemplatePayload(publishValues, { draft: false });
    const response = publishValues.id && publishValues.draft
      ? await apiPatch(`/api/v2/manager/mock_templates/${publishValues.id}`).send(payload)
      : await apiPost("/api/v2/manager/mock_templates").send(payload);

    const created = response.body?.data;
    const targetStatus = values.enabled ? "enabled" : "disabled";
    const createdMock = created ? mapTemplateToTeacherMock(created) : null;
    invalidateCache(targetStatus, "draft");
    if (createdMock) setMocks((current) => [createdMock, ...current]);
    setStatusCounts((current) => ({
      ...current,
      [targetStatus]: current[targetStatus] + 1,
      draft: publishValues.id && publishValues.draft ? Math.max(current.draft - 1, 0) : current.draft,
    }));
    if (createdMock) {
      setPublicationSuccess({
        name: createdMock.name,
        publicUrl: createdMock.publicUrl,
        questionsCount: createdMock.questions,
        studentsCount: response.body?.studentsCount ?? createdMock.totalStudents ?? 0,
      });
    }
    setActiveStatus(targetStatus);
    setPage(1);
  };

  const handleUpdateViewedMock = async (values, originalQuestions) => {
    if (!values.id) return;

    const response = await apiPatch(`/api/v2/manager/mock_templates/${values.id}`).send({
      mockTemplate: {
        mockTemplatesQuestionsAttributes: mapQuestionsToUpdateAttributes(values.questions, originalQuestions),
      },
    });

    const updated = response.body?.data;
    if (updated) {
      const updatedMock = mapTemplateToTeacherMock(updated);
      setViewMockValues(mapTemplateToFormValues(updated));
      setMocks((current) => current.map((mock) => (mock.id === updatedMock.id ? updatedMock : mock)));
      invalidateCache("enabled", "disabled");
    }
  };

  return (
    <TeacherMocksPage
      activeStatus={activeStatus}
      onStatusChange={setActiveStatus}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      courseId={courseId}
      onCourseChange={setCourseId}
      mocks={mocks}
      pagination={pagination}
      statusCounts={statusCounts}
      isLoadingMocks={isLoadingMocks}
      mockTemplatesError={mockTemplatesError}
      page={page}
      onPageChange={setPage}
      courseOptions={courseOptions}
      isLoadingCourses={isLoadingCourses}
      togglingIds={togglingIds}
      rankingDrawer={rankingDrawer}
      publicationSuccess={publicationSuccess}
      onClosePublicationSuccess={() => setPublicationSuccess(null)}
      isModalOpen={isModalOpen}
      modalMode={modalMode}
      isViewDrawerOpen={isViewDrawerOpen}
      viewMockValues={viewMockValues}
      isLoadingViewMock={isLoadingViewMock}
      onOpenCreateModal={openCreateModal}
      onCloseMockModal={closeMockModal}
      onCloseViewDrawer={closeViewDrawer}
      onToggleEnabled={handleToggleEnabled}
      onViewMock={handleViewMock}
      onCopyMock={handleCopyMock}
      onPublishDraft={handlePublishDraft}
      lastCreatedId={lastCreatedId}
      onOpenMockLink={handleOpenMockLink}
      onViewRanking={handleViewRanking}
      onCloseRankingDrawer={closeRankingDrawer}
      onCreate={handleCreate}
      onAutosave={handleAutosaveDraft}
      onUpdate={handleUpdateViewedMock}
    />
  );
}
